import { DropdownOption } from '@/common/getDropdownList';
import { Form, useForm } from '@/components/forms';
import Input from '@/components/forms/Input';
import Select from '@/components/forms/Select';
import Textarea from '@/components/forms/Textarea';
import { descriptionRegex, nameValidationRegex } from '@/constants/regex';
import { useDynamicLocale } from '@/hoslog-intl';
import { z } from 'zod';
import useManageCustomerPointSetting from '../../points-configuration/customerTable/hooks/useManageCustomerPointSetting';

const createPointCategorySchema = (isReview: boolean) =>
  z.object({
    userType: isReview ? z.string().optional() : z.string().min(1, { message: 'common.form.validation.required' }),
    categoryType: isReview ? z.string().optional() : z.string().min(1, { message: 'common.form.validation.required' }),
    categoryName: z
      .string()
      .min(1, { message: 'common.form.validation.required' })
      .regex(nameValidationRegex, 'common.form.validation.nameOnly'),
    description: z
      .string()
      .min(1, { message: 'common.form.validation.required' })
      .refine((val) => val == null || val === '' || descriptionRegex.test(val), {
        message: 'common.form.validation.nameOnly',
      }),
    reviewRewardPoint: isReview
      ? z
          .string()
          .min(1, { message: 'common.form.validation.required' })
          .regex(/^\d+$/, { message: 'common.form.validation.invalid' })
          .refine((val) => Number(val) >= 0, { message: 'common.form.validation.invalid' })
      : z.string().optional(),
  });

export type ManagePointCategoryFormValues = {
  userType?: string;
  categoryType?: string;
  categoryName: string;
  description?: string;
  reviewRewardPoint?: string;
};

export type ManagePointCateggoryFormValues = ManagePointCategoryFormValues;

export const ManagePointCategoryView = ({
  defaultValues,
  userTypeOptions,
  categoryTypeOptions,
  categoryId,
  isReview = false,
  programCode,
  existingDescription,
}: {
  defaultValues: ManagePointCategoryFormValues;
  userTypeOptions: DropdownOption[];
  categoryTypeOptions: DropdownOption[];
  categoryId: string;
  isReview?: boolean;
  programCode?: string;
  existingDescription?: string;
}) => {
  const schema = createPointCategorySchema(isReview);
  const form = useForm({
    schema,
    mode: 'onBlur',
    defaultValues,
  });
  const { t, locale } = useDynamicLocale();
  const { mutateAsync } = useManageCustomerPointSetting();

  const categoryTitleLabel =
    locale === 'ja'
      ? t('pointManagement.pointsConfiguration.managePointCategory.label.categoryName')
      : 'Category Title';
  const reviewRewardPointLabel = locale === 'ja' ? 'レビュー報酬ポイント' : 'Review Reward Point';
  const reviewRewardPointPlaceholder =
    locale === 'ja' ? 'レビュー報酬ポイントを入力してください' : 'Enter review reward point';

  return (
    <Form
      form={form}
      onSubmit={async (data) => {
        if (isReview) {
          await mutateAsync({
            categoryId,
            programCode: programCode ?? 'CUSTOMER_REVIEW',
            category: data.categoryName || defaultValues?.categoryName || '',
            rewardPoint: data.reviewRewardPoint,
            description: data.description ?? existingDescription ?? '',
          });
        } else {
          await mutateAsync({
            categoryId,
            programCode: programCode ?? 'CUSTOMER_REFERRAL',
            category: data.categoryName,
            description: data.description ?? '',
          });
        }
      }}
    >
      <div className="flex flex-col w-full gap-4.5">
        {isReview ? (
          <>
            <Input
              label={categoryTitleLabel}
              placeholder={t('common.form.placeholder.categoryName')}
              {...form.register('categoryName')}
              disabled
              maxLength={50}
            />
            <Input
              inputMode="numeric"
              type="text"
              onlyNumbers
              label={reviewRewardPointLabel}
              placeholder={reviewRewardPointPlaceholder}
              maxLength={6}
              {...form.register('reviewRewardPoint')}
            />
            <Textarea
              label="pointManagement.pointsConfiguration.managePointCategory.label.description"
              placeholder={t('pointManagement.pointsConfiguration.managePointCategory.placeholder.description')}
              {...form.register('description')}
              maxLength={200}
            />
          </>
        ) : (
          <>
            <Select
              label="pointManagement.pointsConfiguration.managePointCategory.label.userType"
              {...form.register('userType')}
              options={userTypeOptions}
              disabled
            />
            <Select
              label="pointManagement.pointsConfiguration.managePointCategory.label.categoryType"
              {...form.register('categoryType')}
              options={categoryTypeOptions}
              disabled
            />
            <Input
              label="pointManagement.pointsConfiguration.managePointCategory.label.categoryName"
              placeholder={t('common.form.placeholder.categoryName')}
              {...form.register('categoryName')}
              maxLength={50}
            />
            <Textarea
              label="pointManagement.pointsConfiguration.managePointCategory.label.description"
              placeholder={t('pointManagement.pointsConfiguration.managePointCategory.placeholder.description')}
              {...form.register('description')}
              maxLength={200}
            />
          </>
        )}
      </div>
    </Form>
  );
};
